/**
 * PlanMapHost — the onboarding plan's own map (`spec-onboarding-3d-plan-map-lock`, R4).
 *
 * A react-map-gl map with a react-three-map overlay, deliberately NOT `MapCanvas` and NOT
 * `TwinScene`. Being a separate map is what keeps the live twin's camera code off it: the mode
 * reset and `flyToTarget` act on `MapCanvas`'s map or on
 * `TwinModelStore.mapRef`, and this host never publishes its map there (TwinScene would, from inside
 * any canvas it is rendered in, which is why it is never rendered here).
 *
 * The canvas draws through the twin's post-processing chain (`PostProcessChain`, from `@repo/ui`), so
 * the plan looks like the twin it stands for (`spec-onboarding-3d-plan-post-translucency`, R3), in the
 * machine look its height gives (`spec-onboarding-persistent-scene-model-look`): one
 * `MachineLookScope` per canvas sets the machines, the lights and the occlusion strength from it.
 *
 * It passes no prop for a setting the lock owns (zoom and pitch limits, interaction handlers):
 * react-map-gl calls a setter only when such a prop changes, so with none passed it can never fight
 * `usePlanMapLock`.
 *
 * `spec-onboarding-3d-plan-plan-host-fixes` adds what the paused-map spike found missing: the map
 * follows its container's size (mapbox-gl 3.7 follows only the window), and it asks for a frame once
 * its content is ready (react-three-map's overlay mounts its children only in a map render, and an
 * idle map draws none). Its 3D canvas never draws outside a map render: a parent's re-render does
 * not reach the `Canvas`, whose props are stable, and R3F's loop is held at `"never"` with every
 * invalidation sent to the map (`planHostGuards.ts` says why). On unmount it releases the
 * geometries, materials and textures its content drew, which shared ones (the cached model geometry,
 * drei's model cache) would otherwise hold its renderer by.
 *
 * `spec-onboarding-3d-plan-renderer-switch` (R2.1): it tells its host whether the plan is drawing, has
 * drawn, or could not be drawn and why (`onDrawState`), so a pane never sits blank without a reason.
 *
 * `spec-onboarding-persistent-scene-stage` (R5): its canvas is never focusable, locked or released.
 */
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';
import { Map, useMap as useMapGl } from 'react-map-gl/mapbox';
import type { ErrorEvent, MapEvent } from 'react-map-gl/mapbox';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useFrame, useStore, useThree } from '@react-three/fiber';
import { Canvas, useMap } from 'react-three-map';
import { useUserStore } from '@repo/front-end-state';
import type { PostProcessSwitches } from '@repo/ui';
import Config from '../../../common/Config';
import { usePlanMapLock } from './usePlanMapLock';
import type { PlanCameraMap } from './usePlanMapLock';
import type { PlanPose, PlanViewState } from './planMapLock';
import { PlanViewContext } from './PlanHeightScale';
import { MachineLookPostProcessChain, MachineLookScope } from './MachineLook';
import type { PlanViewContextValue } from './PlanHeightScale';
import {
  collectDrawnResources,
  createDrawnResources,
  createHostContentStore,
  holdFrameloop,
  holdFrameloopAtCreation,
  releaseResources,
} from './planHostGuards';
import type { FrameloopStore, HostContentStore } from './planHostGuards';
import {
  PLAN_DRAW_TIMEOUT_MS,
  PLAN_WEBGL_FAILED_MESSAGE,
  warnPlanDrawFailed,
} from './planDrawState';
import type { PlanDrawCause, PlanDrawState } from './planDrawState';

export { PLAN_DRAW_TIMEOUT_MS } from './planDrawState';
export type { PlanDrawCause, PlanDrawState } from './planDrawState';

/** The map's public calls the host makes beyond the lock's. */
type PlanHostMap = PlanCameraMap & {
  getContainer(): HTMLElement;
  resize(): unknown;
  triggerRepaint(): void;
  getCenter(): { lng: number; lat: number };
  project(lngLat: { lng: number; lat: number }): { x: number; y: number };
  unproject(point: [number, number]): { lng: number; lat: number };
  jumpTo(options: { center: { lng: number; lat: number } }): unknown;
};

export const PLAN_MAP_TITLE = 'Floor plan, viewed from above';

export interface PlanMapHostProps {
  /** The 3D content's anchor. */
  origin: { latitude: number; longitude: number };
  /**
   * Where the camera starts. Pitch starts at 0 unless the host starts unlocked (`planView` false)
   * and gives one: the opening view's picture starts at the saved view's tilt.
   */
  initialView: {
    longitude: number;
    latitude: number;
    zoom: number;
    bearing: number;
    pitch?: number;
  };
  planView?: boolean;
  animate?: boolean;
  /**
   * False holds Mapbox's own pan, scroll zoom and touch zoom off while locked, for a host whose
   * camera follows something else (the 3D read-only plan). Defaults to true.
   */
  mapGestures?: boolean;
  /**
   * With `planView` false, the lock's free camera instead of `off`: heights follow the tilt
   * (`spec-onboarding-persistent-scene-lock-poses`, R1). Defaults to false.
   */
  free?: boolean;
  /** Where the lock's next leave ends, eased in its one move (R2); absent, the recorded pitch. */
  leavePose?: PlanPose | null;
  /** Where the lock's next enter ends, flat (R3); absent, the camera only flattens. */
  enterPose?: PlanPose | null;
  /** Defaults to the app's basemap. A changed value reloads the style, as on any react-map-gl map. */
  mapStyle?: string;
  onMapError?: (error: { message: string }) => void;
  /**
   * The loaded map, for a host that drives its camera (the 3D read-only plan jumps it to its view);
   * `null` once it is gone. Called once per map, only once the lock has resolved its first state, so
   * a camera written from here already sits inside the lock's limits (a zoom past Mapbox's default
   * max of 22 would otherwise be clamped and never rewritten).
   */
  onMap?: (map: MapboxMap | null) => void;
  /**
   * Whether the 3D layer has started drawing: `true` once react-three-map's canvas is live inside a
   * map render, `false` before then and if that canvas unmounts. A WebGL context lost while the canvas
   * stays mounted is not reported. A map can be loaded and handed out by `onMap` with
   * this still `false` (the layer starts late, or never, when the host warns after its bound). Told on
   * each change, starting `false`.
   */
  onLive?: (live: boolean) => void;
  /**
   * Renderer-switch R2.1: where the drawing stands, told on each change, starting `drawing` (or
   * `failed: no-token` at once). `drawn` once the content is ready and the 3D layer live. Before
   * that: `webgl` or `map-error` for a map error before the map has loaded (a tile's is not one), and `timeout` when
   * nothing has drawn `PLAN_DRAW_TIMEOUT_MS` after mount. A later draw replaces a `map-error` or a
   * `timeout`; nothing after `drawn` changes it (a later error is warned, as before). Only a host
   * given this runs the timer. Retry is the caller's: it remounts the host.
   */
  onDrawState?: (state: PlanDrawState) => void;
  /**
   * The plan view's state (`usePlanMapLock`), told on each change: a host that sets map values of its
   * own once the lock is `off` or `free` (Position twin's free camera) waits for it here.
   */
  onPlanViewState?: (state: PlanViewState) => void;
  /** Hide the map (`visibility: hidden`) while it still loads and lays out. Defaults to false. */
  hidden?: boolean;
  /**
   * Hide the street map only (`floor-plan-mode-bar`, R4.3): Mapbox's own canvas. The plan is drawn by
   * react-three-map's overlay on a canvas of its own, so it stays drawn and usable — unlike `hidden`,
   * which hides the whole container, overlay and all. No style reload, so turning it back on shows the
   * map at once, at the same camera. Defaults to false.
   */
  basemapHidden?: boolean;
  /**
   * The twin's post-processing chain, its occlusion strength from this canvas's machine look: on (the
   * default), off, or each effect switched. Only the measuring rigs pass anything but the default.
   */
  postProcessing?: boolean | PostProcessSwitches;
  /**
   * Keep the drawing buffer after a frame, so the canvases can be read (the corner plan's capture
   * host, `spec-onboarding-3d-plan-corner-plan-snapshot`, R4.2): the map's and the 3D overlay's,
   * which react-three-map draws on a canvas of its own. Absent: the defaults (off).
   */
  preserveDrawingBuffer?: boolean;
  /**
   * Both canvases' pixels per CSS pixel (the capture host passes 1). Absent: the window's ratio for
   * the map, and react-three-fiber's default for the overlay.
   */
  pixelRatio?: number;
  children?: ReactNode;
}

/** The Mapbox token, resolved as MapCanvas resolves it: env config first, then the Auth0 claim. */
export const usePlanMapToken = (): string => {
  const { authMetadata } = useUserStore();
  return Config.mapboxToken || authMetadata?.tokens?.mapboxToken || '';
};

let warnedNoResizeObserver = false;
let warnedReleaseFailed = false;
let warnedNeverLive = false;

/**
 * R2: how many map renders the host asks for, one per `idle`, while react-three-map's layer has not
 * yet drawn its canvas (about 10 s at 60 frames a second). Past it, the host stops asking and warns.
 */
const MAX_START_ASKS = 600;

// As MapCanvas: clear before each frame so nothing accumulates on the overlay. A constant, so the
// `Canvas`'s props never change on a re-render. With a stencil buffer, which the plan's walls mark so
// a machine's turn ring is not drawn under them (`plan-machine-rotate-ring`, R2.2).
const CANVAS_GL = { autoClear: true, stencil: true };
/** The same, keeping the drawing buffer so the overlay can be read after a frame (the capture host). */
const CANVAS_GL_PRESERVED = { autoClear: true, stencil: true, preserveDrawingBuffer: true };

interface HostContent {
  children: ReactNode;
  context: PlanViewContextValue;
  postProcessing: boolean | PostProcessSwitches;
}

/**
 * R3.2: R3F draws only inside a map's render; every invalidation asks the map for one. The lasting
 * hold, which also follows R3F's later changes (its `configure` on a re-render, its size measure).
 * R3F's `onCreated` took a first one before anything could draw (`PlanCanvas`). It also tells the
 * host the canvas is live (R2).
 */
const FrameloopGuard = ({ onLive }: { onLive: (live: boolean) => void }) => {
  const store = useStore();
  const map = useMap();
  // A layout effect, so it holds before the content's own effects; under R3F's inherited StrictMode
  // the development double mount releases and takes it again.
  useLayoutEffect(
    () => holdFrameloop(store as unknown as FrameloopStore, map as { triggerRepaint(): void }),
    [store, map],
  );
  useEffect(() => {
    onLive(true);
    return () => onLive(false);
  }, [onLive]);
  return null;
};

/**
 * R4: what the content drew, gathered on each map render while mounted, is released on unmount
 * only. Gathered ahead, so the release never depends on the scene still holding the content when
 * React tears it down.
 */
const ReleaseOnUnmount = () => {
  const scene = useThree(s => s.scene);
  const drawn = useRef(createDrawnResources());
  useFrame(() => collectDrawnResources(scene, drawn.current));
  useEffect(() => {
    const resources = drawn.current;
    return () => {
      const { failed, firstError } = releaseResources(resources);
      if (failed > 0 && !warnedReleaseFailed) {
        warnedReleaseFailed = true;
        console.warn('[PlanMapHost] a resource could not be released', { failed, firstError });
      }
    };
  }, []);
  return null;
};

/**
 * R3.1: the children, the plan-view context and the post-processing switch, read from the host's
 * store inside the 3D tree. The plan view's context wraps the chain too, so the canvas's one machine
 * look reaches its occlusion strength (`spec-onboarding-persistent-scene-model-look`, R6).
 */
const HostScene = ({ content }: { content: HostContentStore<HostContent> }) => {
  const { children, context, postProcessing } = useSyncExternalStore(
    content.subscribe,
    content.get,
    content.get,
  );
  return (
    <PlanViewContext.Provider value={context}>
      <MachineLookScope>
        {children}
        {/* The composer keeps a stencil buffer for the walls' marks (`plan-machine-rotate-ring`). */}
        {postProcessing === true ? (
          <MachineLookPostProcessChain stencil />
        ) : postProcessing ? (
          <MachineLookPostProcessChain effects={postProcessing} stencil />
        ) : null}
      </MachineLookScope>
    </PlanViewContext.Provider>
  );
};

/**
 * `spec-onboarding-persistent-scene-stage` R5: the canvas out of the tab order from the moment the
 * map exists. Mapbox makes it focusable (`tabindex="0"`) at construction, and `load` comes only once
 * the style and first tiles have, or never on a failed draw; react-map-gl renders the map's children
 * once the instance exists, so this runs before the first paint. Removed, not `-1` (a `-1` canvas
 * still takes a press's focus from its pane: renderer-switch R3.5). `onLoad` removes it again.
 */
const CanvasOutOfTabOrder = () => {
  const mapRef = useMapGl().current;
  useLayoutEffect(() => {
    try {
      mapRef?.getMap().getCanvas().removeAttribute('tabindex');
    } catch {
      // The map is being removed.
    }
  }, [mapRef]);
  return null;
};

/**
 * R3.1: its props are numbers, a store and a state setter, none of which change, so a parent's
 * re-render stops here.
 */
const PlanCanvas = memo(
  ({
    latitude,
    longitude,
    content,
    onLive,
    preserveDrawingBuffer,
    pixelRatio,
  }: {
    latitude: number;
    longitude: number;
    content: HostContentStore<HostContent>;
    onLive: (live: boolean) => void;
    /** The host's own two capture props: the 3D overlay is a canvas of its own, read beside the map's. */
    preserveDrawingBuffer?: boolean;
    pixelRatio?: number;
  }) => {
    const mapRef = useMapGl().current;
    // R3.2: R3F's root calls this in its first layout effect, before its loop can draw, so the hold
    // starts before R3F's own first frame and before the first map render's size sync.
    const onCreated = useCallback(
      (state: { get(): unknown; set(partial: object): void }) => {
        const map = mapRef?.getMap();
        if (map) holdFrameloopAtCreation(state, map);
      },
      [mapRef],
    );
    return (
      <Canvas
        latitude={latitude}
        longitude={longitude}
        overlay
        flat
        // react-three-map's type allows only "always" or "demand"; R3F is held at "never" from
        // `onCreated` on.
        frameloop="demand"
        gl={preserveDrawingBuffer ? CANVAS_GL_PRESERVED : CANVAS_GL}
        {...(pixelRatio !== undefined ? { dpr: pixelRatio } : {})}
        onCreated={onCreated}
      >
        <FrameloopGuard onLive={onLive} />
        <ReleaseOnUnmount />
        <HostScene content={content} />
      </Canvas>
    );
  },
);
PlanCanvas.displayName = 'PlanCanvas';

export const PlanMapHost = ({
  origin,
  initialView,
  planView = true,
  animate = true,
  mapGestures = true,
  free = false,
  leavePose = null,
  enterPose = null,
  mapStyle = Config.mapboxStyleUrl,
  onMapError,
  onMap,
  onLive,
  onDrawState,
  onPlanViewState,
  hidden = false,
  basemapHidden = false,
  postProcessing = true,
  preserveDrawingBuffer,
  pixelRatio,
  children,
}: PlanMapHostProps) => {
  const token = usePlanMapToken();
  const [map, setMap] = useState<PlanHostMap | null>(null);
  const lock = usePlanMapLock(map, {
    planView,
    animate,
    mapGestures,
    free,
    leavePose,
    enterPose,
  });
  const onPlanViewStateRef = useRef(onPlanViewState);
  onPlanViewStateRef.current = onPlanViewState;
  useEffect(() => {
    if (lock.settled) onPlanViewStateRef.current?.(lock.state);
  }, [lock.settled, lock.state]);
  const getLockState = lock.getState;
  const warnedErrors = useRef(new Set<string>());
  const onMapRef = useRef(onMap);
  onMapRef.current = onMap;
  // Unmount only: the map is handed out once the lock has resolved, below.
  useEffect(() => () => onMapRef.current?.(null), []);

  // Content waits for the first resolved state, so no frame is drawn in an unlocked view.
  const [contentReady, setContentReady] = useState(false);
  useEffect(() => {
    if (
      map &&
      lock.settled &&
      (lock.state === 'locked' || lock.state === 'off' || lock.state === 'free')
    ) {
      setContentReady(true);
    }
  }, [map, lock.settled, lock.state]);
  useEffect(() => {
    if (map && contentReady) onMapRef.current?.(map as unknown as MapboxMap);
  }, [map, contentReady]);

  // `floor-plan-mode-bar` R4.3: the street map's own canvas, never the container (the plan's overlay
  // is in the container). A map not yet loaded is hidden as it arrives; its draw state is untouched.
  // Only a real change asks for a frame, so a shown map's load costs nothing extra.
  useEffect(() => {
    if (!map) return;
    try {
      const canvas = map.getCanvas();
      const visibility = basemapHidden ? 'hidden' : '';
      if (canvas.style.visibility === visibility) return;
      canvas.style.visibility = visibility;
      map.triggerRepaint();
    } catch {
      // The map is being removed: nothing to show or hide.
    }
  }, [map, basemapHidden]);

  // R2: react-three-map's overlay mounts the content only in a map render, and a map already idle
  // when the content becomes ready draws none, so ask for one. Once per map, when it turns ready.
  useEffect(() => {
    if (!map || !contentReady) return;
    try {
      map.triggerRepaint();
    } catch {
      // The map is being removed: nothing to draw, and the host is unmounting.
    }
  }, [map, contentReady]);

  // R2: that one request can land before react-three-map's layer is ready to draw its canvas, and the
  // map then goes idle with the plan empty. Until the canvas reports it is live, each `idle` asks
  // again; once it is, every change in it asks the map itself (the frameloop guard).
  const [canvasLive, setCanvasLive] = useState(false);
  const onLiveRef = useRef(onLive);
  onLiveRef.current = onLive;
  useEffect(() => {
    onLiveRef.current?.(canvasLive);
  }, [canvasLive]);
  useEffect(() => {
    if (!map || !contentReady || canvasLive) return undefined;
    let asks = 0;
    const askAgain = () => {
      if (asks >= MAX_START_ASKS) {
        map.off('idle', askAgain);
        if (!warnedNeverLive) {
          warnedNeverLive = true;
          console.warn('[PlanMapHost] the 3D layer did not start drawing');
        }
        return;
      }
      asks += 1;
      try {
        map.triggerRepaint();
      } catch {
        // The map is being removed.
      }
    };
    map.on('idle', askAgain);
    return () => {
      map.off('idle', askAgain);
    };
  }, [map, contentReady, canvasLive]);

  // R1: follow the container, not only the window (Mapbox's own `trackResize` stays on for that).
  // Attached once the map has loaded; a hidden map still lays out, so it is the right size when shown.
  useEffect(() => {
    if (!map) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      if (!warnedNoResizeObserver) {
        warnedNoResizeObserver = true;
        console.warn('[PlanMapHost] no ResizeObserver');
      }
      return undefined;
    }
    const container = map.getContainer();
    const canvas = map.getCanvas();
    // The observer reports on observing; that first report resizes only if the map's canvas is not
    // already that size (to the pixel: the canvas has whole CSS pixels, a flex pane may not). Later
    // reports compare with the last one in the same, unrounded units.
    let applied: { width: number; height: number } | null = null;
    const observer = new ResizeObserver(entries => {
      const entry = entries.find(e => e.target === container);
      if (!entry) return;
      const { width, height } = entry.contentRect;
      const same = applied
        ? width === applied.width && height === applied.height
        : Math.abs(width - canvas.clientWidth) < 1 && Math.abs(height - canvas.clientHeight) < 1;
      applied = { width, height };
      if (same) return;
      if (getLockState() !== 'locked') {
        map.resize();
        return;
      }
      /*
       * Mapbox's resize keeps the centre; a plan's view keeps the pane's top-left, as the 2D plan
       * does when the machine card docks beside it. Until the host's next camera (a render away,
       * behind whatever docked the card), the drawing would sit half the change away from the
       * overlays and the pick. Hold the top-left corner instead. Only while locked: a jump stops
       * the lock's own ease.
       */
      const corner = map.unproject([0, 0]);
      map.resize();
      const moved = map.project(corner);
      if (Math.abs(moved.x) < 0.5 && Math.abs(moved.y) < 0.5) return;
      const centre = map.project(map.getCenter());
      map.jumpTo({ center: map.unproject([centre.x + moved.x, centre.y + moved.y]) });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map, getLockState]);

  // Renderer-switch R2.1: the draw state. Drawn holds once reached; a failure before it is held
  // until a draw replaces it. Read by the map's error handler through a ref, so it knows the loaded map.
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (contentReady && canvasLive) setDrawn(true);
  }, [contentReady, canvasLive]);
  const [failure, setFailure] = useState<PlanDrawCause | null>(null);
  const loadedRef = useRef(false);
  loadedRef.current = !!map;
  const drawnRef = useRef(drawn);
  drawnRef.current = drawn;
  const reportsDrawState = !!onDrawState;
  useEffect(() => {
    if (!reportsDrawState) return undefined;
    const timer = setTimeout(() => {
      if (!drawnRef.current) setFailure(f => f ?? 'timeout');
    }, PLAN_DRAW_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [reportsDrawState]);
  const drawState: PlanDrawState = !token
    ? { kind: 'failed', cause: 'no-token' }
    : drawn
      ? { kind: 'drawn' }
      : failure
        ? { kind: 'failed', cause: failure }
        : { kind: 'drawing' };
  const drawStateKey = drawState.kind === 'failed' ? `failed:${drawState.cause}` : drawState.kind;
  const onDrawStateRef = useRef(onDrawState);
  onDrawStateRef.current = onDrawState;
  useEffect(() => {
    if (drawState.kind === 'failed') warnPlanDrawFailed(drawState.cause);
    onDrawStateRef.current?.(drawState);
    // Told once per change of state, by its key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawStateKey]);

  const context = useMemo(
    () => ({ getHeightScale: lock.getHeightScale, getState: lock.getState }),
    [lock.getHeightScale, lock.getState],
  );

  // R3.1: the content reaches the 3D tree through a store, never through the `Canvas`'s props. A
  // layout effect, so a commit's children are in the 3D tree in the same commit.
  const shown = contentReady ? children : null;
  const [content] = useState(() =>
    createHostContentStore<HostContent>({ children: shown, context, postProcessing }),
  );
  useLayoutEffect(() => {
    const current = content.get();
    if (
      current.children !== shown ||
      current.context !== context ||
      current.postProcessing !== postProcessing
    ) {
      content.set({ children: shown, context, postProcessing });
    }
  });

  // R2.1: no token is said through the draw state (and warned there), never with the token.
  if (!token) return null;

  return (
    <Map
      mapboxAccessToken={token}
      initialViewState={{ ...initialView, pitch: planView ? 0 : (initialView.pitch ?? 0) }}
      mapStyle={mapStyle}
      antialias
      // Only when given, so every other host keeps react-map-gl's defaults (R4.2).
      {...(preserveDrawingBuffer !== undefined ? { preserveDrawingBuffer } : {})}
      {...(pixelRatio !== undefined ? { pixelRatio } : {})}
      locale={{ 'Map.Title': PLAN_MAP_TITLE }}
      style={{ width: '100%', height: '100%', ...(hidden ? { visibility: 'hidden' } : {}) }}
      onLoad={(e: MapEvent) => {
        /*
         * `spec-onboarding-persistent-scene-stage` R5: every plan map out of the tab order, decided
         * in this host once (`CanvasOutOfTabOrder` from the map's creation; again here). Mapbox
         * makes its canvas focusable though it sits in an aria-hidden layer. Removed before the lock
         * first records it, so releasing the lock restores none.
         */
        try {
          e.target.getCanvas().removeAttribute('tabindex');
        } catch {
          // The map is being removed.
        }
        setMap(e.target as unknown as PlanHostMap);
      }}
      onError={(e: ErrorEvent) => {
        const error = e.error;
        if (!warnedErrors.current.has(error.message)) {
          warnedErrors.current.add(error.message);
          console.warn('[PlanMapHost] map error', error);
        }
        // R2.1: an error before the map has loaded is a failed draw; after it, only the warning. A
        // tile's fetch (Mapbox fires it with the `tile`) is not: the map draws with the tiles that
        // came, such as with none where the token answers 403, and the bound still catches a map that
        // never draws.
        const tileError = !!(e as ErrorEvent & { tile?: unknown }).tile;
        if (!loadedRef.current && !drawnRef.current && !tileError) {
          const cause = error.message === PLAN_WEBGL_FAILED_MESSAGE ? 'webgl' : 'map-error';
          setFailure(f => f ?? cause);
        }
        onMapError?.(error);
      }}
    >
      <CanvasOutOfTabOrder />
      <PlanCanvas
        latitude={origin.latitude}
        longitude={origin.longitude}
        content={content}
        onLive={setCanvasLive}
        preserveDrawingBuffer={preserveDrawingBuffer}
        pixelRatio={pixelRatio}
      />
    </Map>
  );
};
